import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthDialog } from "@/components/AuthDialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAlertCount } from "@/components/AlertsPanel";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import {
  Waves, Loader2, CalendarRange, Users, Lock, LinkIcon, Globe, ArrowLeft, Share2,
} from "lucide-react";
import { toast } from "sonner";

const VIS_META = {
  public:  { label: "Public",       icon: Globe,    desc: "Open to the community" },
  friends: { label: "Friends only", icon: Lock,     desc: "Only the host’s friends and invitees" },
  link:    { label: "Link only",    icon: LinkIcon, desc: "Anyone with the link" },
};

function fmtPlanned(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "long", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function PlanLanding() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { open: openAuth } = useAuthDialog();
  const alertCount = useAlertCount();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    queueMicrotask(() => setError(""));
    try {
      const { data } = await http.get(`/plans/${id}`);
      queueMicrotask(() => setPlan(data));
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Plan not found";
      queueMicrotask(() => setError(msg));
    }
  };

  useEffect(() => { load(); }, [id]);

  const isAuthed = !!(user && user.id);
  const isHost = isAuthed && plan && plan.host_id === user.id;
  const isJoined = isAuthed && plan && plan.joiners?.some((j) => j.id === user.id);
  const full = plan && (plan.joiners?.length || 0) >= plan.max_joiners;

  const doJoin = async () => {
    if (!isAuthed) return openAuth("login");
    queueMicrotask(() => setBusy(true));
    try {
      const { data } = await http.post(`/plans/${id}/join`);
      queueMicrotask(() => setPlan(data));
      toast.success("You're in");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not join");
    } finally { queueMicrotask(() => setBusy(false)); }
  };

  const doLeave = async () => {
    queueMicrotask(() => setBusy(true));
    try {
      const { data } = await http.post(`/plans/${id}/leave`);
      queueMicrotask(() => setPlan(data));
      toast("You left the plan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not leave");
    } finally { queueMicrotask(() => setBusy(false)); }
  };

  const share = async () => {
    const url = window.location.href;
    const text = plan ? `Surf with me — ${plan.beach_name}, ${fmtPlanned(plan.planned_at)}${plan.note ? `\n${plan.note}` : ""}` : "Join my surf plan on Popoyo.app";
    if (navigator.share) {
      try { await navigator.share({ title: "Popoyo.app · Surf Plan", text, url }); return; } catch (_e) { /* dismissed */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch (_e) { toast.info("Share this link", { description: url }); }
  };

  const VisIcon = plan ? VIS_META[plan.visibility]?.icon || Globe : Globe;

  useSEO({
    title: plan
      ? `Surf plan · ${plan.beach_name} — Popoyo.app`
      : "Surf plan — Popoyo.app",
    description: plan
      ? `${fmtPlanned(plan.planned_at)} at ${plan.beach_name}${plan.note ? ` — ${plan.note}` : ""}. Join via Popoyo.app.`
      : "Join a community surf plan in Popoyo via Popoyo.app.",
    path: `/plan/${id}`,
  });

  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--sand)]">
      <Header alertCount={alertCount} />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1 text-sm text-[color:var(--ink-2)] hover:text-[color:var(--ink)] mb-3">
          <ArrowLeft className="w-4 h-4" /> Back to Popoyo.app
        </Link>

        {!plan && !error && (
          <div className="clay p-10 flex items-center justify-center text-[color:var(--ink-2)]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading plan…
          </div>
        )}

        {error && (
          <div data-testid="plan-error" className="clay p-8 text-center space-y-3">
            <p className="text-base font-semibold text-[color:var(--ink)]">{error}</p>
            {error.toLowerCase().includes("sign in") && !isAuthed && (
              <Button onClick={() => openAuth("login")} className="pill bg-[color:var(--navy)] text-white">Sign in to view</Button>
            )}
            <Button variant="outline" onClick={() => navigate("/")} className="pill">Back home</Button>
          </div>
        )}

        {plan && (
          <article className="clay p-6 md:p-8 space-y-5" data-testid="plan-landing">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[color:var(--teal)]/10 text-[color:var(--teal)] flex items-center justify-center shrink-0">
                <Waves className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="eyebrow flex items-center gap-1.5"><VisIcon className="w-3 h-3" />{VIS_META[plan.visibility]?.label}</p>
                <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
                  {plan.beach_name}
                </h1>
                <p className="text-[color:var(--ink-2)] mt-1 flex items-center gap-1.5">
                  <CalendarRange className="w-4 h-4" /> {fmtPlanned(plan.planned_at)}
                </p>
                <p className="text-[color:var(--ink-2)] text-sm mt-0.5">
                  Hosted by <strong>@{plan.host_username || plan.host_name}</strong>
                </p>
              </div>
            </div>

            {plan.note && (
              <p className="text-[color:var(--ink)] whitespace-pre-line bg-[color:var(--sand-2)]/60 rounded-2xl p-4">{plan.note}</p>
            )}

            <div>
              <p className="eyebrow mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {plan.joiners.length}/{plan.max_joiners} joined</p>
              <div className="flex flex-wrap gap-2">
                {plan.joiners.map((j) => (
                  <span key={j.id} data-testid={`joiner-${j.id}`} className="px-3 py-1 rounded-full bg-[color:var(--sand-2)] text-xs font-medium">
                    @{j.username || j.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-[color:var(--ink-2)]/10">
              <Button onClick={share} variant="outline" data-testid="plan-share" className="pill">
                <Share2 className="w-4 h-4 mr-1.5" /> Share
              </Button>
              {isHost ? (
                <span className="text-sm text-[color:var(--ink-2)] self-center px-2">You’re the host</span>
              ) : isJoined ? (
                <Button onClick={doLeave} disabled={busy} variant="outline" data-testid="plan-leave" className="pill">
                  {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Leave
                </Button>
              ) : (
                <Button onClick={doJoin} disabled={busy || full} data-testid="plan-join" className="pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90">
                  {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} {full ? "Plan full" : isAuthed ? "Join plan" : "Sign in to join"}
                </Button>
              )}
            </div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
